import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { CyclePhase } from "@match/shared";
import { showSymbolWarnings } from "@/components/notice";
import { PHASE_LABEL } from "@/components/session";
import { Button, ErrorNote, Field, SidePanel, inputClass } from "@/components/ui";
import { ManualBaseFields, manualBase } from "@/components/prop-onboarding";
import { ApiError, api, errorMessage } from "@/lib/api";
import { usePropFirms, useRefreshDirectory, useServers } from "@/lib/queries";
import type { Account, ClientDetail } from "@/lib/types";

/*
 * «CREA FASE» SU UN CONTO PROP CHE NON NE HA UNA NON CONCLUSA (fase eliminata,
 * conto rimasto libero). Dal menu «…» della riga del conto, scheda cliente.
 *
 * **Una strada sola**: `POST /sessions` col conto prop che c'e' gia', con tutti i
 * suoi controlli — proxy del cliente, conto libero, ciclo del cliente e della
 * fase giusta, simbolo, saldo del broker letto dal vivo (non letto = rifiuto,
 * nessuna Fase). La Fase parte da zero come ogni Fase nuova: P&L, DD, DD
 * giornaliero e target contano dalla sua creazione.
 *
 * Il ciclo: uno APERTO del cliente — attivo, senza una Fase non conclusa, che
 * non aspetta il conto di una fase (quello si registra da «Aggiungi conto») — e
 * in Fase 2 o Funded, perche' una Fase 1 apre sempre un ciclo nuovo e il
 * backend rifiuterebbe; oppure nessuno, senza contabilita'.
 */

type ClientCycle = ClientDetail["cycles"][number];

const PHASES: CyclePhase[] = ["PHASE_1", "PHASE_2", "FUNDED"];
const NON_CONCLUSE = ["ACTIVE", "PAUSED", "AWAITING_BROKER", "AWAITING_PAYOUT"];

/** I cicli a cui si puo' attaccare una Fase da qui: quelli che `POST /sessions` accetta. */
export function openCyclesForNewSession(cycles: ClientCycle[], awaitingCycleIds: ReadonlySet<string>): ClientCycle[] {
  return cycles.filter(
    (c) => c.state === "ACTIVE" && c.currentPhase !== "PHASE_1" && !awaitingCycleIds.has(c.id) && !c.sessions.some((s) => NON_CONCLUSE.includes(s.state)),
  );
}

/**
 * I CONTI BROKER DAVVERO LIBERI del cliente: senza una Fase non conclusa. Lo
 * stato viene da `GET /accounts` (`liveOf`), perche' i conti della scheda
 * cliente (`GET /clients/:id`) non lo portano. Finche' non e' arrivato
 * (`liveKnown` falso) nessuno e' «libero»: meglio un elenco vuoto per un istante
 * che proporre un conto occupato. Un calcolo solo per «Crea fase», «Aggiungi
 * conto», «Aggiungi conto broker» e «Sostituisci conto broker».
 */
export const freeBrokerAccounts = (accounts: Account[], liveOf: (id: string) => unknown, liveKnown: boolean): Account[] =>
  liveKnown ? accounts.filter((a) => a.role === "BROKER" && !liveOf(a.id)) : [];

/**
 * La voce «Crea fase» c'e' solo sui conti prop senza una Fase non conclusa.
 * `liveKnown` falso = le Fasi dei conti non sono ancora arrivate (`GET /accounts`):
 * nel dubbio la voce non c'e', invece di comparire su un conto occupato.
 */
export const canCreateSession = (a: Pick<Account, "role">, live: unknown, liveKnown: boolean): boolean => liveKnown && a.role === "PROP" && !live;

const cycleLabel = (c: ClientCycle) => `${c.label ?? c.propFirm.name} · ${PHASE_LABEL(c.currentPhase)} · ${c.propFirm.name} ${c.preset.name}`;

export function CreateSessionPanel({
  open,
  account,
  clientId,
  clientName,
  brokerAccounts,
  openCycles,
  onClose,
}: {
  open: boolean;
  /** Il conto prop su cui nasce la Fase. */
  account: Account;
  clientId: string;
  clientName: string;
  /** I conti broker LIBERI del cliente (senza una Fase non conclusa). */
  brokerAccounts: Account[];
  /** I cicli aperti a cui si puo' attaccare (`openCyclesForNewSession`). */
  openCycles: ClientCycle[];
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const refresh = useRefreshDirectory();
  const firms = usePropFirms();
  const servers = useServers();
  const brokerLiberi = brokerAccounts;

  /** "NONE" = senza contabilita', altrimenti l'id del ciclo. Nessuna preselezione del ciclo. */
  const [cycleChoice, setCycleChoice] = useState<string>("NONE");
  const [presetId, setPresetId] = useState("");
  const [phaseChoice, setPhaseChoice] = useState<CyclePhase>("PHASE_1");
  /** "" = nessun broker: la Fase nasce in attesa del broker. */
  const [brokerAccountId, setBrokerAccountId] = useState("");
  const [symbol, setSymbol] = useState("");
  /** La base a mano del primo periodo: compare solo dopo un BALANCE_NOT_READ, come in «Aggiungi conto broker». */
  const [baseAmount, setBaseAmount] = useState("");
  const [baseNote, setBaseNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const ciclo = openCycles.find((c) => c.id === cycleChoice) ?? null;
  const allPresets = (firms.data ?? []).flatMap((f) => f.presets.map((p) => ({ ...p, firmName: f.name })));
  const preset = allPresets.find((p) => p.id === presetId);
  // Col ciclo programma e fase sono i suoi: non si scelgono.
  const phase: CyclePhase = ciclo ? ciclo.currentPhase : phaseChoice;
  const phase2Available = !preset || preset.phases >= 2;
  const effPhase: CyclePhase = !ciclo && phase === "PHASE_2" && !phase2Available ? "PHASE_1" : phase;

  // Il simbolo: mappato sul server prop, e su quello del broker se ce n'e' uno.
  const propServer = servers.data?.find((s) => s.id === account.serverId);
  const brokerServer = brokerAccountId ? servers.data?.find((s) => s.id === brokerAccounts.find((a) => a.id === brokerAccountId)?.serverId) : undefined;
  const symbolOptions = !propServer
    ? []
    : propServer.symbols
        .map((s) => s.canonical)
        .filter((c) => !brokerServer || brokerServer.symbols.some((b) => b.canonical === c))
        .sort();
  const effSymbol = symbolOptions.includes(symbol) ? symbol : "";

  const problems = [!ciclo && !presetId && "programma", !effSymbol && "simbolo"].filter(Boolean) as string[];

  const submit = async () => {
    if (problems.length) return;
    const { base, fieldError, issue } = manualBase(baseAmount, baseNote);
    if (fieldError) return;
    if (issue) return setError(issue);
    setBusy(true);
    setError(null);
    setErrorCode(null);
    try {
      const r = await api.sessions.create({
        clientId,
        propAccountId: account.id,
        ...(brokerAccountId && { brokerAccountId }),
        canonicalSymbol: effSymbol,
        phase: effPhase,
        // Sempre esplicito: l'API, senza, vale `true`.
        accounting: !!ciclo,
        ...(ciclo ? { cycleId: ciclo.id } : { presetId }),
        // Il numero col punto, come lo legge la regola ("0,5" e "0.5" valgono uguale).
        ...(base !== null ? { baseAmount: base, baseNote: baseNote.trim() } : {}),
      });
      showSymbolWarnings(r);
      await refresh();
      navigate(`/fasi/${r.session.id}`);
    } catch (e) {
      setError(errorMessage(e));
      setErrorCode(e instanceof ApiError ? e.code : null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      title="Crea fase"
      subtitle={`${clientName} · ${account.label} · ${account.mt5Login}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Annulla
          </Button>
          <Button variant="primary" disabled={busy || problems.length > 0} onClick={() => void submit()} title={problems.length ? `Manca: ${problems.join(", ")}` : undefined}>
            {busy ? "Creazione…" : "Crea fase"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field
          label="Contabilità"
          hint={
            !openCycles.length
              ? "Nessun ciclo aperto in Fase 2 o Funded senza una fase: la fase nasce senza contabilità, e una Fase 1 può avere il suo ciclo dopo («Crea ciclo»)."
              : undefined
          }
        >
          {(id) => (
            <select id={id} value={cycleChoice} onChange={(e) => setCycleChoice(e.target.value)} className={inputClass}>
              <option value="NONE">Senza contabilità</option>
              {openCycles.map((c) => (
                <option key={c.id} value={c.id}>
                  {cycleLabel(c)}
                </option>
              ))}
            </select>
          )}
        </Field>

        {ciclo ? (
          <p className="rounded-card border border-line bg-surface p-3 text-xs text-muted">
            Programma {ciclo.propFirm.name} {ciclo.preset.name}, {PHASE_LABEL(ciclo.currentPhase)}: quelli del ciclo.
          </p>
        ) : (
          <>
            <Field label="Programma">
              {(id) => (
                <select id={id} value={presetId} onChange={(e) => setPresetId(e.target.value)} className={inputClass}>
                  <option value="" disabled>
                    Scegli il programma
                  </option>
                  {allPresets.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.firmName} · {p.name}
                    </option>
                  ))}
                </select>
              )}
            </Field>
            <Field label="Fase">
              {(id) => (
                <select id={id} value={effPhase} onChange={(e) => setPhaseChoice(e.target.value as CyclePhase)} className={inputClass}>
                  {PHASES.filter((p) => p !== "PHASE_2" || phase2Available).map((p) => (
                    <option key={p} value={p}>
                      {PHASE_LABEL(p)}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          </>
        )}

        <Field label="Conto broker" hint={!brokerLiberi.length ? "Nessun conto broker libero: la fase nasce in attesa del broker, e si aggancia dopo dalla sua scheda." : undefined}>
          {(id) => (
            <select id={id} value={brokerAccountId} onChange={(e) => setBrokerAccountId(e.target.value)} className={inputClass}>
              <option value="">Nessuno (la fase nasce in attesa del broker)</option>
              {brokerLiberi.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.label} · {a.mt5Login}
                </option>
              ))}
            </select>
          )}
        </Field>

        <Field label="Simbolo" hint={propServer && !symbolOptions.length ? "Nessun simbolo mappato su tutti i server di questa fase: mappalo dalla pagina Server." : undefined}>
          {(id) => (
            <select id={id} value={effSymbol} onChange={(e) => setSymbol(e.target.value)} className={inputClass}>
              <option value="" disabled>
                Scegli il simbolo
              </option>
              {symbolOptions.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          )}
        </Field>

        <ErrorNote>{error}</ErrorNote>
        {errorCode === "BALANCE_NOT_READ" && (
          <ManualBaseFields
            currency={brokerAccounts.find((a) => a.id === brokerAccountId)?.currency ?? "broker"}
            amount={baseAmount}
            onAmount={setBaseAmount}
            note={baseNote}
            onNote={setBaseNote}
          />
        )}
      </div>
    </SidePanel>
  );
}
